/* eslint-disable operator-linebreak */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import cc from 'classnames';
import { observer } from 'mobx-react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { FORM_ERROR } from 'final-form';
import { useHistory } from 'react-router-dom';
import LoaderPage from 'ui/LoaderPage/LoaderPage';
import ErrorPage from 'ui/ErrorPage/ErrorPage';
import { AxiosStatus } from 'api/axiosConfig';
import NoData from 'ui/NoData/NoData';
import api from 'api/api';
import { StatusModal, OTPVerificationModal } from 'components/modals';
import { FormType as FormTypeOTP } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import StatusLabel from 'ui/TableInputs/StatusLabel/StatusLabel';
import { IApiResponse } from 'utils/models';
import LoaderForTable from 'ui/LoaderForTable/LoaderForTable';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import SourceAccountClick from 'ui/TableInputs/SourceAccountEvent/SourceAccountEvent';
import RecallRequest from 'ui/TableButtons/RecallButton/RecallButton';
import { formattedDate } from 'utils/formatters';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import routePaths from 'routes/routePaths';
import { NETWORK_REQUIRED } from 'utils/constants';
import { IRecallPrepaidCard, IRecallPrepaidResponse, ISubmitPrepaidBody } from '../../RecallPrepaid';
import RecallPepaidFilter, { ISearchParams } from './RecallPrepaidFilters';

import noDataImg from 'assets/img/no_recall_data.png';

const RecallSingleCardLoad = () => {
  // Loading states
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;
  const failureLoaded = loadingStatus === AxiosStatus.FAILURE;
  const [loading, setLoading] = useState(false);
  const [showNoDataWindow, setShowNoDataWindow] = useState(false);
  const [OTPModalOpen, setOTPModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<IRecallPrepaidCard>();
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  // TableState
  const gridRef = useRef<AgGridReact<IRecallPrepaidCard>>(null);
  const [rowData, setRowData] = useState<IRecallPrepaidCard[]>([]);
  const [searchParams, setSearchParams] = useState<ISearchParams>({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  // Set Table Data
  const requestDataForTable = () => {
    gridRef.current?.api?.showLoadingOverlay();
    const requestBody = {
      transType: 'PREPAIDCARD-TRANSFER',
      requestType: 'SINGLE',
      pageNumber,
      pageSize,
    };
    const searchParamBody = {
      filter: true,
      ...searchParams,
      ...requestBody,
    };
    const query =
      Object.values(searchParams).length > 0
        ? api.post('transfer/myRequest', searchParamBody)
        : api.post('transfer/myRequest', requestBody);
    query
      .then((data: IRecallPrepaidResponse) => {
        setRowData(data.transactions);
        setTotalRows(data.size);
        if (data.size === 0) {
          gridRef.current?.api?.showNoRowsOverlay();
        } else {
          gridRef.current?.api.hideOverlay();
        }
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLoadingStatus(AxiosStatus.FAILURE);
        gridRef.current?.api.hideOverlay();
      });
  };
  useEffect(requestDataForTable, [pageNumber, pageSize, searchParams]);

  // TODO fix double request for first render to display NoData component
  useEffect(() => {
    setLoadingStatus(AxiosStatus.PENDING);
    const requestBody = {
      transType: 'PREPAIDCARD-TRANSFER',
      requestType: 'SINGLE',
      filter: false,
      pageNumber,
      pageSize,
    };
    api
      .post('transfer/myRequest', requestBody)
      .then((data: IRecallPrepaidResponse) => {
        setLoadingStatus(AxiosStatus.SUCCESS);
        if (data.size === 0) {
          setShowNoDataWindow(true);
        } else {
          setShowNoDataWindow(false);
        }
      })
      .catch((error: IApiResponse) => {
        setLoadingStatus(AxiosStatus.FAILURE);
        console.error(error);
      });
  }, []);

  const handleRecall = (data: IRecallPrepaidCard) => () => {
    setOTPModalOpen(true);
    setTransactions(data);
    return api.post('/customer/otp/generate');
  };

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.myRequests);
  };
  // State for Transaction Recall
  const closeOTPModal = () => {
    setOTPModalOpen(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    setOTPModalOpen(false);
  }, []);

  //  Handle Submit recall request
  const handleSubmitRecall = (data: IRecallPrepaidCard, otp: string) => {
    const body: ISubmitPrepaidBody = {
      transactionId: data.transactionId,
      password: otp,
      passwordType: 'OTP',
    };
    setLoading(true);
    return api.post('transfer/transactionrecall', body);
  };

  // Handle Approval with OTP SUBMIT
  const onOtpSubmit = useCallback(
    (value: IRecallPrepaidCard) => (otp: string | undefined, form: FormTypeOTP | undefined) => {
      setSubmitStatus(AxiosStatus.PENDING);
      api
        .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
        .then(() => {
          handleSubmitRecall(value, String(otp))
            .then((response: IApiResponse) => {
              setLoading(false);
              closeOTPModal();
              setSuccessMsg(response.description);
              setSubmitStatus(AxiosStatus.SUCCESS);
              requestDataForTable();
            })
            .catch((err: IApiResponse) => {
              onRejectedSubmit(err);
            });
        })
        .catch((error: IOTPError) => {
          setOTPModalError(error);
          if (error.failureCount === 0 && form) {
            form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
          }
          return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
        });
    },
    [],
  );
  const onResend = () => api.post('/customer/otp/generate');
  // RenderCols & Columns shape
  const renderActions = (prop: ICellRendererParams<IRecallPrepaidCard, number | string>) => {
    if (prop.data && prop.data?.workFlowStatus === 'PENDING') {
      return <RecallRequest onClickRecall={handleRecall(prop.data)} />;
    }
    return false;
  };
  const renderCustomerId = (prop: ICellRendererParams<IRecallPrepaidCard, number | string>) => {
    if (prop.data) {
      return <div>{prop.data.transactionId}</div>;
    }
    return false;
  };

  const renderCardNumber = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data && prop.data?.lastCardDigits) {
      return <div>{`****${String(prop.data?.lastCardDigits)}`}</div>;
    }
    return null;
  };

  const renderScheduleId = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data) {
      return <div>{prop.data.scheduleId}</div>;
    }
    return null;
  };
  const renderMobileNumber = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data) {
      return <div>{prop.data?.phoneNumber}</div>;
    }
    return null;
  };
  const renderAccount = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data) {
      return <SourceAccountClick acctNumber={prop.data?.sourceAccount} acctType={prop.data?.SourceAccountType} />;
    }
    return null;
  };
  const renderAmount = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data) {
      const amount = prop.data.transactionAmount;
      const formattedAmount = `GHS ${amount.toFixed(2)}`;
      return <div>{formattedAmount}</div>;
    }
    return null;
  };
  const renderNarration = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data) {
      return <div>{prop.data.transactionNarration}</div>;
    }
    return null;
  };
  const renderDate = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data && prop.data.createDate) {
      const formatDate = formattedDate(prop.data.createDate);
      return <div>{formatDate}</div>;
    }
    return null;
  };

  const renderStatus = (prop: ICellRendererParams<IRecallPrepaidCard>) => {
    if (prop.data?.workFlowStatus === 'PENDING') {
      return <StatusLabel type="pending" />;
    }
    if (prop.data?.workFlowStatus === 'IN_PROGRESS') {
      return <StatusLabel type="in progress" />;
    }
    if (prop.data?.workFlowStatus === 'APPROVED') {
      return <StatusLabel type="approved" />;
    }
    if (prop.data?.workFlowStatus === 'REJECTED') {
      return <StatusLabel type="rejected" />;
    }
    if (prop.data?.workFlowStatus === 'FAILED') {
      return <StatusLabel type="failed" />;
    }
    if (prop.data?.workFlowStatus === 'RECALLED') {
      return <StatusLabel type="recalled" />;
    }
    return <StatusLabel type="unknown" />;
  };

  const columnDefs: ColDef[] = [
    { field: 'transactionID', headerName: 'Transaction ID', flex: 1, minWidth: 190, cellRenderer: renderCustomerId },
    { field: 'cardNumber', headerName: 'Card Number', flex: 1, minWidth: 190, cellRenderer: renderCardNumber },
    { field: 'scheduleId', headerName: 'Schedule ID', flex: 1, minWidth: 190, cellRenderer: renderScheduleId },
    { field: 'sourceAccount', headerName: 'Source Account', flex: 1, minWidth: 150, cellRenderer: renderAccount },
    { field: 'mobileNumber', headerName: 'Mobile Number', flex: 1, minWidth: 130, cellRenderer: renderMobileNumber },
    { field: 'amount', headerName: 'Amount', flex: 1, minWidth: 130, cellRenderer: renderAmount },
    { field: 'narration', headerName: 'Narration', flex: 1, minWidth: 100, cellRenderer: renderNarration },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      initialWidth: 120,
      maxWidth: 150,
      cellRenderer: renderStatus,
    },
    { field: 'created', headerName: 'Created', flex: 1, minWidth: 200, cellRenderer: renderDate },
    { field: 'action', headerName: '', width: 150, cellRenderer: renderActions },
  ];

  // Pagination & Filters handlers
  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const handleSubmitFilters = (query: ISearchParams) => {
    setPageNumber(1);
    setSearchParams(query);
  };

  return (
    <>
      {pendingLoading && <LoaderPage />}
      {failureLoaded && <ErrorPage />}
      {successLoaded && (
        <>
          {/* <pre>{JSON.stringify(rowData, null, 2)}</pre> */}
          {showNoDataWindow ? (
            <NoData img={noDataImg} title="No requests yet" subtitle="Once you create a request, it appears here" />
          ) : (
            <>
              <RecallPepaidFilter onSubmitFilters={handleSubmitFilters} />
              <div className={cc('tableWrapper', 'ag-theme-material')}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  loadingOverlayComponent={LoaderForTable}
                  columnDefs={columnDefs}
                  defaultColDef={{ resizable: true }}
                  enableCellTextSelection
                  headerHeight={30}
                  rowHeight={70}
                  undoRedoCellEditing
                  suppressCellFocus
                  suppressRowHoverHighlight
                  suppressScrollOnNewData
                  domLayout="autoHeight"
                />
              </div>
              <TablePagination
                count={Math.ceil(totalRows / pageSize)}
                page={pageNumber}
                pageSize={pageSize}
                totalCount={totalRows}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
              />
              {OTPModalOpen && transactions && (
                <OTPVerificationModal
                  open={OTPModalOpen}
                  onClose={closeOTPModal}
                  onSubmit={onOtpSubmit(transactions)}
                  onResend={onResend}
                  OTPModalError={OTPModalError}
                  loading={loading}
                />
              )}
              <StatusModal
                open={showStatusModal}
                status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                onClose={onCloseStatusModal}
                onRetry={onCloseStatusModal}
                onRetryText="Close"
              >
                {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
              </StatusModal>
            </>
          )}
        </>
      )}
    </>
  );
};

export default observer(RecallSingleCardLoad);
